import axios, { AxiosResponse } from 'axios';
import { ExaminersList, UserIds, UserLogged } from '../../../components/ExaminersStructure/ExaminersStructure.types';
import { AssessmentSortById, RawAssessmentSessionProgress } from '../../../pages/Assessment/Assessment.types';
import { ExamTaskGroupSolutionWithTasks } from '../../../models/assessment.types';
import { ExamTaskGroupSolutionsResponse, ExamTaskSolutionsIdResponse } from './assessments.types';
import { addParam, downloadFile } from '../../../utils/servicesHelper';
import EnvWindow from '../../../utils/env';
import {
  PatchLimits,
  ProcessLimits,
} from '../../../pages/Sessions/SessionsParametersDialog/SessionsProcessLimits/SessionsProcessLimits.types';
import {
  ExamTaskGroupProcessesSchedule,
  ExamTaskGroupProcessesScheduleUpdate,
  ExamSheetTaskGroupScheduleVerificationStatus,
} from '../../../pages/Sessions/SessionsParametersDialog/SessionsProcessSchedule/SessionsProcessSchedule.types';
import { FlagTypes } from '../../../components/SystemSettings/SystemFlags/Subcomponents/Flag.types';
import {
  ToleranceRangeChange,
  ToleranceRangeResponse,
} from '../../../pages/Sessions/SessionsParametersDialog/ToleranceRange/ToleranceRange.types';
import { ExaminerBlockingLimit } from '../../../pages/Sessions/SessionsParametersDialog/ExaminerBlockingLimits/ExaminerBlockingLimits.types';
import { Tile } from '../../../pages/Dashboard/Dashboard.types';
import { AssessmentSession, AssessmentSessionForStatsDateResponse } from '../../../models/session.types';
import { IMessageRecipientsList, IRecipientList, MessageRecipients } from '../../../models/communication.types';
import {
  AssessmentSessionDateResponse,
  CalendarEventTypeId,
  ProcedureDto,
} from '../../../pages/Calendar/Calendar.types';

export const getAssessmentSessions = async (): Promise<AssessmentSession[]> => {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}`);
  return response.data;
};

export const getParams = (pageNumber: number, pageSize: number, name: string) => {
  let params = '';
  params += addParam('pageNumber', pageNumber ? pageNumber : '');
  params += addParam('pageSize', pageSize ? pageSize : '');
  params += addParam('name', name ? name : '');
  return params;
};

export const getRecipient = async (
  selectedExamTaskGroupId: string | undefined,
  zwoStructure?: boolean,
): Promise<MessageRecipients> => {
  let params = '';
  params += addParam('zwoStructure', zwoStructure ? zwoStructure : '');
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/${selectedExamTaskGroupId}/Recipient?${params}`,
  );
  return response.data;
};

export const getRecipientAdmin = async (
  pageNumber: number,
  pageSize: number,
  name: string,
): Promise<IMessageRecipientsList> => {
  const params = getParams(pageNumber, pageSize, name);
  const response = await axios.get(`${EnvWindow.REACT_APP_COMMUNICATION_API_URL}/user?${params}`); //TODO ADD Page size when bd will fix it
  return response.data;
};

export async function getAssessmentExaminerStructure(
  assessmentSessionId: string,
): Promise<AxiosResponse<ExaminersList>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/examinerStructure`,
  );
}

export const getSampleAssessmentExaminerStructure = async (): Promise<AxiosResponse<ExaminersList>> => {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS__SAMPLE_ASSESSMENT_GROUP_API_URL}/Structure`);
};

export const disableExaminer = async (examinerPermissionId: string): Promise<void> => {
  return await axios.patch(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExaminerPermission/${examinerPermissionId}/blocked/true`,
  );
};

export async function enableExaminer(examinerPermissionId: string): Promise<void> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExaminerPermission/${examinerPermissionId}/blocked/false`,
  );
}

export const disableOrEnableExaminingTeam = async (examiningTeamChairmanId: string, block?:boolean): Promise<void> => {
  return await axios.patch(
    `${EnvWindow.REACT_APP_EXAMS__EXAMINING_TEAM_API_URL}/${examiningTeamChairmanId}/block/${block}`,
  );
};

export const enableExaminersInexaminersTreemap = async (
  blockadeTypeId?: string[],
): Promise<void> => {
  const blockageTypeIdsQuery = blockadeTypeId
    ? `?${blockadeTypeId.map((id: string) => `blockageTypeIds=${id}`).join('&')}`
    : '';
  return await axios.patch(
    `${EnvWindow.REACT_APP_EXAMS__EXAMINING_TEAM_API_URL}/blocked/false/many${blockageTypeIdsQuery}`,
    JSON.stringify([]),
    { headers: { 'Content-Type': 'application/json' } },
  );
};

export const disableExaminingTeamInExaminersTreemap = async (examiningTeamId: string): Promise<void> => {
  return await axios.patch(`${EnvWindow.REACT_APP_EXAMS__EXAMINING_TEAM_API_URL}/${examiningTeamId}/blocked`);
};

export function getExamGradeRuleUrl(ruleId: string): string {
  return `${EnvWindow.REACT_APP_EXAMS__EXAM_GRADE_RULE_API_URL}/${ruleId}/document`;
}

export function isExamGradeRuleFileExisting(ruleId: string) {
  return axios.get(getExamGradeRuleUrl(ruleId));
}

export function getExamSessionGradeRuleDocUrl(assessmentSessionId: string): string {
  return `${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/AssessmentSession/${assessmentSessionId}/gradeRuleDoc`;
}

export async function downloadGradeRulesFile(assessmentSessionId: string, fileName: string) {
  downloadFile(getExamSessionGradeRuleDocUrl(assessmentSessionId), fileName);
}

export async function getAssessmentSessionProgress(
  assessmentSessionId: string,
  examinerIds: string[],
): Promise<AxiosResponse<RawAssessmentSessionProgress>> {
  const examinerIdsQuery = examinerIds.map((id: string) => `examinerIds=${id}`).join('&');
  return await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/progress?${examinerIdsQuery}`,
  );
}

export async function getExamTasksWithCriteria(
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId?: string,
): Promise<ExamTaskGroupSolutionWithTasks> {
  const examTaskGroupSolutionScoreVersionIdPart = examTaskGroupSolutionScoreVersionId
    ? `&examTaskGroupSolutionScoreVersionId=${examTaskGroupSolutionScoreVersionId}`
    : '';
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/examTasksCriteria?examTaskGroupSolutionScoreId=${examTaskGroupSolutionScoreId}${examTaskGroupSolutionScoreVersionIdPart}`,
  );
  return response.data;
}

export async function setSpecialCase(
  examTaskGroupSolutionScoreId: string,
  value: boolean,
): Promise<AxiosResponse<boolean>> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/SpecialCase`,
    {
      examTaskGroupSolutionScoreId,
      value,
    },
  );
  return response;
}

const getAlertParam = (alerts?: string, value?: string) => {
  return alerts && value && alerts.indexOf(value) !== -1 ? 'true' : '';
};

export async function getExamTaskSolutionsForExaminer(
  assessmentSessionId: string,
  functionId: string,
  functionEnum: string,
  processId: string,
  signal?: AbortSignal,
  withProgressBar?: boolean,
  pageSize = 0,
  pageNumber = 20,
  sortByProperty?: AssessmentSortById,
  isDescending?: boolean,
  tasksPoints?: any,
  flag?: string,
  examTaskGroupSolutionScoreId?: string,
  status?: string,
  pointsFrom?: string,
  pointsTo?: string,
  progress?: string,
  alerts?: string,
  downloadDateFrom?: string,
  downloadDateTo?: string,
  updateDateFrom?: string,
  updateDateTo?: string,
  assessmentQualityId?: string,
  examinerName?: string,
  examTaskNumbersWithNoAnswer?: string,
): Promise<AxiosResponse<ExamTaskGroupSolutionsResponse>> {
  const flagFilter =
    flag &&
    flag
      .split(',')
      .map((e) => FlagTypes.find((f) => f.name === e)!.id.toString())
      .join();
  let params = '';
  params += addParam('withProgressBar', withProgressBar ? withProgressBar : '');
  params += addParam('examTaskGroupSolutionScoreId', examTaskGroupSolutionScoreId ? examTaskGroupSolutionScoreId : '');
  params += addParam('examinerName', examinerName ? examinerName : '');
  params += addParam('status', status ? status : '');
  params += addParam('assessmentQualityId', assessmentQualityId ? assessmentQualityId : '');
  params += addParam('pointsFrom', pointsFrom ? pointsFrom : '');
  params += addParam('pointsTo', pointsTo ? pointsTo : '');
  params += addParam('isNoAnswer', examTaskNumbersWithNoAnswer ? 'true' : '');
  params += addParam('progress', progress ? progress : '');
  params += addParam('downloadDateFrom', downloadDateFrom ? downloadDateFrom : '');
  params += addParam('downloadDateTo', downloadDateTo ? downloadDateTo : '');
  params += addParam('updateDateFrom', updateDateFrom ? updateDateFrom : '');
  params += addParam('updateDateTo', updateDateTo ? updateDateTo : '');
  params += addParam('flag', flag ? flagFilter : '');
  params += addParam('isCommentOnCriteria', getAlertParam(alerts, '1'));
  params += addParam('isCommentOnImage', getAlertParam(alerts, '2'));
  params += addParam('isCommentOffPage', getAlertParam(alerts, '3'));
  params += addParam('isSpecialCase', getAlertParam(alerts, '4'));
  params += addParam('hasClosedSpecialCase', getAlertParam(alerts, '5'));
  params += addParam('isControlProcessType', getAlertParam(alerts, '6'));
  params += addParam('isScoringAdjusted', getAlertParam(alerts, '7'));
  params += addParam('isScoreChanged', getAlertParam(alerts, '8'));
  params += addParam('withMessage', getAlertParam(alerts, '9'));
  params += addParam('sortBy', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? 'true' : 'false');

  const response = await axios.post(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_API_URL}/filter/assessmentSession/${assessmentSessionId}/desk/${functionId}/function/${functionEnum}/process/${processId}?pageSize=${pageSize}&pageNumber=${pageNumber}${params}`,
    {
      signal: signal ?? undefined,
      tasksPoints
    }
  );
  return response;
}

export async function getExamTaskSolutionsIdList(
  assessmentSessionId: string,
  functionId: string,
  functionEnum: string,
  processId: string,
  onlyWithUnassessedTasks?: boolean,
): Promise<ExamTaskSolutionsIdResponse> {
  const response = await axios({
    url: `${
      EnvWindow.REACT_APP_SCORING_API_URL
    }/AssessmentSession/${assessmentSessionId}/desk/${functionId}/function/${functionEnum}/process/${processId}${
      onlyWithUnassessedTasks ? '?onlyWithUnassessedTasks=true' : ''
    }`,
    method: 'GET',
  });
  return response.data;
}

export const getSheetGradeRuleName = async (assessmentSessionId: string): Promise<AxiosResponse<string | null>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/examSheet/gradeRuleDocumentName`,
  );
  return response;
};

export async function getProcessLimits(sheetId: string | number, isExaminerView?: boolean): Promise<ProcessLimits> {
  if (isExaminerView) {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/process`);
    return { assessmentSessionProcesses: [response.data] };
  } else {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/processes`);
    return response.data;
  }
}

export async function changeProcessLimits(body: PatchLimits, isExaminer?: boolean): Promise<AxiosResponse> {
  if (isExaminer) {
    const response = await axios.patch(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/process`, body);
    return response.data;
  } else {
    const response = await axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/processes`, body);
    return response.data;
  }
}

export async function getProcessesScheduleBySheetId(
  sheetId: string | number,
): Promise<ExamTaskGroupProcessesSchedule[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/schedule`);
  const result = response.data.map((schedule: ExamTaskGroupProcessesSchedule) => {
    return {
      ...schedule,
      assessmentSessionDateFrom: schedule.assessmentSessionDateFrom
        ? new Date(new Date(schedule.assessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      assessmentSessionDateTo: schedule.assessmentSessionDateTo
        ? new Date(new Date(schedule.assessmentSessionDateTo).setSeconds(0, 0))
        : null,
      trainingAssessmentSessionDateFrom: schedule.trainingAssessmentSessionDateFrom
        ? new Date(new Date(schedule.trainingAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      qualificationAssessmentSessionDateFrom: schedule.qualificationAssessmentSessionDateFrom
        ? new Date(new Date(schedule.qualificationAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      scoringAssessmentSessionDateFrom: schedule.scoringAssessmentSessionDateFrom
        ? new Date(new Date(schedule.scoringAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
    };
  });
  return result;
}

export async function getProcessesScheduleBySession(): Promise<ExamTaskGroupProcessesSchedule[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/schedule`);
  const result = [response.data].map((schedule: ExamTaskGroupProcessesSchedule) => {
    return {
      ...schedule,
      assessmentSessionDateFrom: schedule.assessmentSessionDateFrom
        ? new Date(new Date(schedule.assessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      assessmentSessionDateTo: schedule.assessmentSessionDateTo
        ? new Date(new Date(schedule.assessmentSessionDateTo).setSeconds(0, 0))
        : null,
      trainingAssessmentSessionDateFrom: schedule.trainingAssessmentSessionDateFrom
        ? new Date(new Date(schedule.trainingAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      qualificationAssessmentSessionDateFrom: schedule.qualificationAssessmentSessionDateFrom
        ? new Date(new Date(schedule.qualificationAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
      scoringAssessmentSessionDateFrom: schedule.scoringAssessmentSessionDateFrom
        ? new Date(new Date(schedule.scoringAssessmentSessionDateFrom).setSeconds(0, 0))
        : null,
    };
  });
  return result;
}

export async function updateProcessesSchedule(
  schedules: ExamTaskGroupProcessesScheduleUpdate[],
  sheetId?: string | number,
): Promise<ExamSheetTaskGroupScheduleVerificationStatus> {
  if (sheetId) {
    const response = await axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/schedule`, schedules);
    return response.data;
  } else {
    const response = await axios.patch(
      `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/schedule`,
      schedules[0],
    );
    return response.data;
  }
}

export async function getExamSheetProcessLimitToleranceRange(
  examSheetId: string,
  assessmentSessionProcessTypeId: string,
): Promise<AxiosResponse<ToleranceRangeResponse>> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/AssessmentSessionProcessType/${assessmentSessionProcessTypeId}`,
  );
  return response;
}

export async function getAssessmentSessionStateId(): Promise<AxiosResponse<ToleranceRangeResponse>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/AssessmentSession/state`);
  return response;
}

export async function getIsTraingSession(): Promise<AxiosResponse<boolean>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/AssessmentSession/isTraining`);
  return response;
}

export async function getAssessmentSessionProcessLimitToleranceRange(
  assessmentSessionProcessTypeId: string,
): Promise<AxiosResponse<ToleranceRangeResponse>> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/AssessmentSessionProcessType/${assessmentSessionProcessTypeId}`,
  );
  return response;
}

export async function putExamTaskGroupProcessLimitToleranceRange(
  examTaskGroupId: string,
  assessmentSessionProcessTypeId: string,
  toleranceRange: ToleranceRangeChange,
): Promise<AxiosResponse<boolean>> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/${examTaskGroupId}/AssessmentSessionProcessType/${assessmentSessionProcessTypeId}/tolerance`,
    toleranceRange,
  );
  return response;
}

export async function putAssessmentSessionProcessLimitToleranceRange(
  assessmentSessionProcessTypeId: string,
  toleranceRange: ToleranceRangeChange,
): Promise<AxiosResponse<boolean>> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/AssessmentSessionProcessType/${assessmentSessionProcessTypeId}/tolerance`,
    toleranceRange,
  );
  return response;
}

export async function getExaminersBlockingLimitsPerSession(): Promise<AxiosResponse<ExaminerBlockingLimit>> {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/BlockingLimits`);
}

export async function getDashboardTiles(
  assessmentSessionId?: string,
  trainingOnly?: boolean,
): Promise<AxiosResponse<Tile>> {
  let params = '';
  params += addParam('assessmentSessionId', assessmentSessionId ? assessmentSessionId : '');
  params += addParam('trainingOnly', trainingOnly ? trainingOnly : 'false');
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/Dashboard?${params}`);
}

export async function getUserStillInAssessmentSession(
  assessmentSessionId: string,
  examinersIds: string[],
): Promise<AxiosResponse<string[]>> {
  return await axios.get(
    `${
      EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL
    }/${assessmentSessionId}/examinerId/validate?examinersIds=${examinersIds.join('&examinersIds=')}`,
  );
}

export async function getAssessmentSessionCalendar(): Promise<AxiosResponse<AssessmentSessionDateResponse[]>> {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/calendar`);
}

export async function getAssessmentSessionsForStats(): Promise<AxiosResponse<AssessmentSessionForStatsDateResponse[]>> {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSession`);
}

export async function getAssessmentSessionEventProcedures(
  assessmentSessionId: string,
  calendarEventTypeId: CalendarEventTypeId,
): Promise<AxiosResponse<ProcedureDto[]>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_CALENDAR_API_URL}/Procedure?assessmentSessionId=${assessmentSessionId}&calendarEventTypeId=${calendarEventTypeId}`,
  );
}

export async function addAssessmentSessionEventProcedure(body: ProcedureDto): Promise<AxiosResponse<ProcedureDto>> {
  return await axios.post(`${EnvWindow.REACT_APP_CALENDAR_API_URL}/Procedure`, body);
}

export async function updateAssessmentSessionEventProcedure(
  body: ProcedureDto,
  procedureId: string,
): Promise<AxiosResponse<ProcedureDto>> {
  return await axios.patch(`${EnvWindow.REACT_APP_CALENDAR_API_URL}/Procedure/${procedureId}`, body);
}

export async function getAssessmentSessionEventProcedure(procedureId: string): Promise<AxiosResponse<ProcedureDto>> {
  return await axios.get(`${EnvWindow.REACT_APP_CALENDAR_API_URL}/Procedure/${procedureId}`);
}

export async function deleteAssessmentSessionEventProcedure(procedureId: string): Promise<AxiosResponse<ProcedureDto>> {
  return await axios.delete(`${EnvWindow.REACT_APP_CALENDAR_API_URL}/Procedure/${procedureId}`);
}
